import { useCallback, useEffect, useState } from "react";
import { CalendarDays, ExternalLink, Plus, Pencil, Trash2, Loader2, Sparkles, User } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Location events board — AI-crawled well-known festivals + user-maintained local
   events. Every event links out to published pages (official-site search links,
   Eventbrite/Ticketmaster/Google Events directories). */

const inp = "w-full border border-zinc-300 bg-white px-3 py-2 text-sm";
const EMPTY = { name: "", category: "festival", timing: "", description: "", venue: "", link: "" };

function EventForm({ initial, onSave, onCancel, categories }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="border border-zinc-300 bg-zinc-50 p-4 space-y-2" data-testid="event-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.name} onChange={set("name")} placeholder="Event name" className={inp} data-testid="event-name" />
        <select value={f.category} onChange={set("category")} className={inp} data-testid="event-category">
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input value={f.timing} onChange={set("timing")} placeholder="When — e.g. March (annual) or 2026-05-12" className={inp} data-testid="event-timing" />
        <input value={f.venue || ""} onChange={set("venue")} placeholder="Venue / area (optional)" className={inp} data-testid="event-venue" />
      </div>
      <textarea value={f.description} onChange={set("description")} placeholder="Short description" rows={2} className={inp} data-testid="event-description" />
      <input value={f.link || ""} onChange={set("link")} placeholder="Official page link (optional, https://…)" className={inp} data-testid="event-link" />
      <div className="flex gap-2">
        <button onClick={() => onSave(f)} disabled={f.name.trim().length < 2} data-testid="event-save"
          className="px-4 py-2 bg-[#34A853] text-white text-xs font-700 disabled:opacity-40">Save event</button>
        <button onClick={onCancel} className="px-4 py-2 text-xs text-muted-foreground">Cancel</button>
      </div>
    </div>
  );
}

export default function GeoEvents({ slug, placeName }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/geo-events/${slug}`).then((r) => setData(r.data)).catch(() => setData({ events: [], directories: [], categories: [] }));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // AI crawl may still be running on first visit — refresh once after a bit
    if (data?.crawl_state === "queued" || data?.crawl_state === "running") {
      const t = setTimeout(load, 20000);
      return () => clearTimeout(t);
    }
  }, [data, load]);

  if (data === null) return <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />;

  const save = async (f, id) => {
    try {
      if (id) await api.put(`/geo-events/id/${id}`, f);
      else await api.post(`/geo-events/${slug}`, f);
      toast.success(id ? "Event updated" : "Event added — thanks for helping travelers!");
      setAdding(false); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save event"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this event?")) return;
    await api.delete(`/geo-events/id/${id}`);
    toast.success("Event removed"); load();
  };

  return (
    <div data-testid="geo-events-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {(data.directories || []).map((d) => (
          <a key={d.label} href={d.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 bg-white text-xs font-700 hover:border-zinc-500"
            data-testid={`event-dir-${d.label.toLowerCase().replace(/\s/g, "-")}`}>
            {d.label} <ExternalLink className="w-3 h-3" />
          </a>
        ))}
        {user && !adding && (
          <button onClick={() => { setAdding(true); setEditing(null); }} data-testid="event-add"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#4285F4] text-white text-xs font-700 hover:opacity-90">
            <Plus className="w-3.5 h-3.5" /> Add a local event
          </button>
        )}
      </div>

      {adding && <div className="mb-4"><EventForm initial={{}} categories={data.categories || []} onSave={(f) => save(f)} onCancel={() => setAdding(false)} /></div>}

      {(data.crawl_state === "queued" || data.crawl_state === "running") && data.events.length === 0 && (
        <div className="border border-zinc-200 bg-white p-6 text-center text-sm text-muted-foreground" data-testid="events-collecting">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Collecting well-known events for {placeName}… check back in a moment.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {data.events.map((e) => (
          <div key={e.id} className="bg-white border border-zinc-300 p-4" data-testid={`event-card-${e.id}`}>
            <div className="flex items-start gap-2">
              <CalendarDays className="w-4 h-4 mt-0.5 shrink-0 text-[#EA4335]" />
              <div className="flex-1 min-w-0">
                <div className="font-700 text-sm">{e.name}</div>
                <div className="text-xs text-muted-foreground font-mono mt-0.5">
                  {[e.timing, e.venue].filter(Boolean).join(" · ")}
                </div>
                {e.description && <p className="text-xs text-zinc-700 mt-1.5">{e.description}</p>}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-100 text-zinc-600">{e.category}</span>
                  <span className={`text-[10px] font-700 px-1.5 py-0.5 inline-flex items-center gap-1 ${e.source === "ai" ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {e.source === "ai" ? <><Sparkles className="w-3 h-3" /> known event</> : <><User className="w-3 h-3" /> community</>}
                  </span>
                  {e.link && (
                    <a href={e.link} target="_blank" rel="noopener noreferrer" data-testid={`event-link-${e.id}`}
                      className="text-[11px] font-700 text-[#4285F4] hover:underline inline-flex items-center gap-0.5">
                      {e.source === "user" && e.link && !e.link.includes("google.com/search") ? "Event page" : "Find official page"} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {(e.is_owner || ["moderator", "admin"].includes(user?.role)) && (
                    <span className="ml-auto flex gap-1">
                      <button onClick={() => { setEditing(e); setAdding(false); }} className="p-1 text-zinc-400 hover:text-primary" data-testid={`event-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(e.id)} className="p-1 text-zinc-400 hover:text-rose-600" data-testid={`event-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </span>
                  )}
                </div>
                {editing?.id === e.id && (
                  <div className="mt-3"><EventForm initial={editing} categories={data.categories || []} onSave={(f) => save(f, e.id)} onCancel={() => setEditing(null)} /></div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {data.events.length === 0 && data.crawl_state === "fresh" && (
        <p className="text-sm text-muted-foreground mt-2" data-testid="events-empty">
          No well-known recurring events on record for {placeName} yet — use the directories above for live listings{user ? ", or add a local event you know about" : ""}.
        </p>
      )}
      {!user && <p className="text-xs text-muted-foreground mt-4">Sign in to add and maintain local events.</p>}
    </div>
  );
}
